import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { getActivityPill, getResultPill, getReactionLabel, getActionMeta, formatShortDate } from '@/lib/activity';

// Detail panel for one activity entry — shared by the journal and the client
// history. Reads fields defensively so it works with both row shapes.
const Row = ({ label, children }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-sm text-foreground break-words">{children}</span>
  </div>
);

const ActivityDetailDialog = ({ entry, onClose }) => {
  if (!entry) return null;

  const meta = getActionMeta(entry.action_type);
  const pill = getActivityPill(entry.display_status, entry.action_type);
  // Call info can arrive nested (journal: call_detail) or flat (history entry
  // / callback). Detect on any call/analysis field being present.
  const call = entry.call_detail
    || (entry.transcript || entry.recording_url || entry.lien_value || entry.call_status
        || entry.outcome || entry.sentiment || entry.besoin_detecte
        || entry.raison_depart || entry.preference_rappel
        ? entry : null);

  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{meta.emoji}</span>
            <span>{meta.label}</span>
            <span className={`ml-1 inline-flex items-center gap-1 text-sm font-medium ${pill.text}`}>
              <span className={`h-2 w-2 rounded-full ${pill.dot}`} />
              {pill.label}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-2">
          <Row label="Date">{formatShortDate(entry.date)}</Row>
          {(entry.client_email || entry.client_name) && (
            <Row label="Client">{entry.client_email || entry.client_name}</Row>
          )}
          {entry.automation_name && <Row label="Automation">{entry.automation_name}</Row>}
          {entry.scenario && <Row label="Scénario">{entry.scenario}</Row>}
        </div>

        {entry.error_message && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Message d'erreur</p>
            <p className="text-sm text-red-700 dark:text-red-300 break-words">{entry.error_message}</p>
          </div>
        )}

        {call && (
          <div className="mt-4 space-y-3">
            <p className="text-sm font-semibold text-foreground">Résultat de l'appel</p>

            {/* Suite donnée : ce que le système a envoyé/fait après l'appel */}
            {(() => {
              const label = getReactionLabel(call.reaction);
              if (!label) return null;
              const ok = call.reaction_status === 'success';
              const failed = call.reaction_status && call.reaction_status !== 'success'
                && call.reaction_status !== 'mocked';
              return (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-xs text-muted-foreground mb-0.5">Suite donnée</p>
                  <p className="text-sm font-medium text-foreground">
                    {ok ? '✅ ' : failed ? '⚠️ ' : ''}{label}
                    {failed && <span className="text-red-600 dark:text-red-400"> (échec d'envoi)</span>}
                  </p>
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-4">
              {call.call_status && <Row label="Statut de l'appel">{call.call_status}</Row>}
              {call.duration != null && call.duration !== '' && (
                <Row label="Durée">{call.duration}s</Row>
              )}
              {call.lien_value && <Row label="Analyse (Bienvenue)">{call.lien_value}</Row>}
              {call.bilan_value && <Row label="Analyse (Bilan)">{call.bilan_value}</Row>}
              {call.reactivation_value && <Row label="Analyse (Réactivation)">{call.reactivation_value}</Row>}
              {call.preference_rappel && <Row label="Préférence rappel">{call.preference_rappel}</Row>}
              {call.sentiment && <Row label="Sentiment">{call.sentiment}</Row>}
              {call.type_probleme && <Row label="Type de problème">{call.type_probleme}</Row>}
              {call.besoin_detecte && <Row label="Besoin détecté">{call.besoin_detecte}</Row>}
              {call.raison_depart && <Row label="Raison du départ">{call.raison_depart}</Row>}
            </div>
            {call.outcome && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Résumé</p>
                <p className="text-sm text-foreground break-words">{call.outcome}</p>
              </div>
            )}
            {call.recording_url && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Enregistrement</p>
                <a href={call.recording_url} target="_blank" rel="noopener noreferrer"
                   className="text-sm text-primary underline break-all">
                  Écouter l'enregistrement
                </a>
              </div>
            )}
            {call.transcript && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Transcription</p>
                <div className="text-sm text-foreground bg-muted/50 rounded-lg p-3 max-h-48 overflow-y-auto whitespace-pre-wrap">
                  {call.transcript}
                </div>
              </div>
            )}
          </div>
        )}

        {!call && entry.action_type === 'ai_call' && !entry.error_message && (
          <p className="mt-4 text-sm text-muted-foreground">
            Résultat d'appel pas encore reçu (l'analyse Callkom arrive après l'appel).
          </p>
        )}

        {entry.engagement && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-semibold text-foreground">Suivi de l'email</p>
            <div className="grid grid-cols-2 gap-4">
              {(() => {
                const rp = getResultPill(entry.result);
                return rp ? (
                  <Row label="Résultat">
                    <span className={`inline-flex items-center gap-1.5 font-medium ${rp.text}`}>
                      <span className={`h-2 w-2 rounded-full ${rp.dot}`} />{rp.label}
                    </span>
                  </Row>
                ) : null;
              })()}
              {entry.engagement.delivered_at && (
                <Row label="Délivré le">{formatShortDate(entry.engagement.delivered_at)}</Row>
              )}
              {entry.engagement.first_opened_at && (
                <Row label="Ouvert le">
                  {formatShortDate(entry.engagement.first_opened_at)}
                  {entry.engagement.open_count > 1 ? ` (×${entry.engagement.open_count})` : ''}
                </Row>
              )}
              {entry.engagement.first_clicked_at && (
                <Row label="Cliqué le">
                  {formatShortDate(entry.engagement.first_clicked_at)}
                  {entry.engagement.click_count > 1 ? ` (×${entry.engagement.click_count})` : ''}
                </Row>
              )}
            </div>
            {entry.engagement.last_clicked_link && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Lien cliqué</p>
                <a href={entry.engagement.last_clicked_link} target="_blank" rel="noopener noreferrer"
                   className="text-sm text-primary underline break-all">
                  {entry.engagement.last_clicked_link}
                </a>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              « Ouvert » est indicatif (Apple Mail peut le déclencher seul) ; « Cliqué » est le signal fiable.
            </p>
          </div>
        )}

        {(entry.action_type === 'email' || entry.action_type === 'sms') && entry.message && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-1">Détail</p>
            <p className="text-sm text-foreground break-words">{entry.message}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDetailDialog;
